import { useRef, useState, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PROJECTS, PROJECT_SCALE, PROJECT_LEDGER, ledgerRowState, LEDGER_STATES } from "../lib/site";

const MATERIAL_FILTERS = ["TMT", "Cement", "Structural", "Plates", "Aggregate"];
const matchesMaterial = (mat, f) =>
  f === "Cement" ? /cement|opc|ppc/i.test(mat) : mat.toLowerCase().includes(f.toLowerCase());

const Card = ({ p, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <motion.article
      ref={ref}
      data-testid={`project-card-${i}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative ${i % 2 === 1 ? "md:mt-28" : ""}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-white/10">
        <motion.img
          style={{ y }}
          src={p.img}
          alt={p.title}
          className="absolute inset-0 w-full h-[124%] object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-700"
        />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-500" />
        <div className="absolute top-6 left-6 z-10 font-grotesk text-xs tracking-[0.2em] text-[#F5F5F0]/80">0{i + 1} / 0{PROJECTS.length}</div>
      </div>
      <div className="flex items-end justify-between mt-6 pb-6 border-b border-white/10">
        <div>
          <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0]">{p.title}</h3>
          <p className="font-body text-sm text-[#8A8A82] mt-1">{p.scope}</p>
        </div>
        <span className="overline text-[#B87333]">{p.location}</span>
      </div>
    </motion.article>
  );
};

export const Projects = () => {
  const [mat, setMat] = useState("All");
  const [state, setState] = useState("All");

  const ledger = useMemo(
    () =>
      PROJECT_LEDGER.filter((r) => {
        if (mat !== "All" && !matchesMaterial(r.material, mat)) return false;
        if (state !== "All" && ledgerRowState(r.location) !== state) return false;
        return true;
      }),
    [mat, state]
  );

  return (
    <section id="projects" data-testid="portfolio-section" className="relative py-24 md:py-36 border-t border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="overline text-[#B87333]">/ Selected Supply</span>
            <h2 className="font-serif text-[clamp(2rem,5vw,4.5rem)] leading-[1.02] tracking-[-0.02em] mt-6">A mammoth<br />execution record</h2>
          </div>
          <p className="max-w-sm font-body text-[#8A8A82] leading-relaxed">
            Bulk material programmes delivered to the developments defining India's skylines, ports, corridors and industry — at a scale few can match.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-16" data-testid="project-scale">
          {PROJECT_SCALE.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#070707] px-6 py-8 md:px-8 md:py-10"
            >
              <div className="font-serif text-3xl md:text-5xl tracking-[-0.02em] text-[#F5F5F0]">{s.value}</div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mt-3">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {PROJECTS.map((p, i) => (
            <Card key={p.title} p={p} i={i} />
          ))}
        </div>

        {/* Project ledger — the massive supply record */}
        <div className="mt-24 md:mt-32" data-testid="project-ledger">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <span className="overline text-[#B87333]">/ Supply Ledger</span>
              <h3 className="font-serif text-[clamp(1.75rem,4vw,3.25rem)] leading-[1.04] tracking-[-0.02em] mt-5">A record at scale</h3>
            </div>
            <p className="max-w-sm font-body text-[#8A8A82] leading-relaxed">
              A selected extract from Vinci Inc.'s executed supply programmes across India — a fraction of {PROJECT_LEDGER.length}+ mandates delivered on time, at volume.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6" data-testid="ledger-filters">
            <div className="flex flex-wrap items-center gap-2">
              {["All", ...MATERIAL_FILTERS].map((m) => (
                <button
                  key={m}
                  data-testid={`ledger-mat-${m.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setMat(m)}
                  className={`rounded-full px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.14em] transition-colors ${mat === m ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <div className="lg:ml-auto flex items-center gap-3">
              <select
                data-testid="ledger-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="bg-transparent border border-white/15 focus:border-[#B87333] outline-none rounded-full px-4 py-2 font-body text-sm text-[#F5F5F0] [&>option]:bg-[#0e0e0e]"
              >
                <option value="All">All states</option>
                {LEDGER_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {(mat !== "All" || state !== "All") && (
                <button
                  data-testid="ledger-clear"
                  onClick={() => { setMat("All"); setState("All"); }}
                  className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#8A8A82] hover:text-[#B87333] transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <p className="font-body text-sm text-[#8A8A82] mb-4" data-testid="ledger-count">
            Showing <span className="text-[#F5F5F0]">{ledger.length}</span> of {PROJECT_LEDGER.length} programmes
          </p>

          <div className="border border-white/10 rounded-sm overflow-hidden">
            <div className="hidden md:grid grid-cols-[1.6fr_1fr_1.2fr_0.7fr_0.5fr_0.7fr] gap-4 px-6 py-4 bg-white/[0.03] border-b border-white/10">
              {["Project", "Location", "Material", "Volume", "Year", "Status"].map((h) => (
                <span key={h} className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">{h}</span>
              ))}
            </div>
            {ledger.length === 0 ? (
              <p className="font-body text-sm text-[#8A8A82] px-6 py-10" data-testid="ledger-empty">No programmes match these filters.</p>
            ) : (
            <div>
              {ledger.map((r, i) => (
                <motion.div
                  key={r.project}
                  data-testid={`ledger-row-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 8) * 0.03, ease: [0.16, 1, 0.3, 1] }}
                  className="grid grid-cols-2 md:grid-cols-[1.6fr_1fr_1.2fr_0.7fr_0.5fr_0.7fr] gap-x-4 gap-y-1 px-6 py-4 border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors group"
                >
                  <span className="font-serif text-base md:text-lg text-[#F5F5F0] col-span-2 md:col-span-1 group-hover:text-[#B87333] transition-colors">{r.project}</span>
                  <span className="font-body text-sm text-[#8A8A82]">{r.location}</span>
                  <span className="hidden md:block font-body text-sm text-[#8A8A82]">{r.material}</span>
                  <span className="font-body text-sm text-[#C9C9C2]">{r.tonnage}</span>
                  <span className="hidden md:block font-body text-sm text-[#8A8A82]">{r.year}</span>
                  <span className={`font-grotesk text-[10px] uppercase tracking-[0.16em] ${r.status === "Delivered" ? "text-[#2FB999]" : "text-[#D8B23E]"}`}>{r.status}</span>
                </motion.div>
              ))}
            </div>
            )}
          </div>
          <p className="font-body text-xs text-[#6f6f68] mt-6">Figures are indicative of programme scale. Full references available to verified partners on request.</p>
        </div>
      </div>
    </section>
  );
};
